import { IAttrValue, IRowData } from "@multiweb/types/contracts";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { IAttrStringValue } from "../../../../Global/Base/DataTypes/IAttrValue";
import { Checkbox } from "../../../UI.BaseControls/Checkbox";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IListDataGrid } from "../../IDataGrid";
import { Column } from "../Columns/Column";
import { ListDataRow } from "./ListDataRow";
import { TableCell } from "./TableCell";

export class CheckedDataRow extends ListDataRow {
    private _checkboxElement: Checkbox;
    //Příznak indikující zkrácení některé z hodnot na řádku.
    private _isSomeValueShortened: boolean;

    public isDisabled = false;

    public get isChecked(): boolean {
        if (this.checkAndLogDisposedUsage("get isChecked")) return false;

        if (this._checkboxElement)
            return this._checkboxElement.checked;
        return this.classList.contains("checkedItem");
    }

    public set isChecked(v: boolean) {
        if (this.checkAndLogDisposedUsage("set isChecked")) return;

        if (this.isDisabled) return;
        if (this._checkboxElement)
            this._checkboxElement.checked = v;
        this.classList.toggle("checkedItem", v);
    }

    public override get isSelected(): boolean {
        if (this.checkAndLogDisposedUsage("get isSelected")) return false;

        return this._dataGrid.selectedRows.indexOf(this) != -1;
    }

    public override set isSelected(value: boolean) {
        if (this.checkAndLogDisposedUsage("set isSelected")) return;

        if (value) {
            if (this._dataGrid.selectedRows.indexOf(this) === -1)
                this._dataGrid.selectedRows.push(this);
        } else {
            this._dataGrid.selectedRows.splice(this._dataGrid.selectedRows.indexOf(this), 1);
            this._dataGrid.deselectRow(this.dataKey);
        }

        if (this.isChecked === value)
            return;

        this.isChecked = value;
    }

    public get values(): string {
        if (this.checkAndLogDisposedUsage("get values")) return "";

        return Array.from(this.children).map((item) => item.textContent).join("").trim();
    }

    /**Pokud je některá z hodnot v řádku zkrácena, vrací true. */
    public get isSomeValueShortened(): boolean {
        if (this.checkAndLogDisposedUsage("get isSomeValueShortened")) return false;

        return this._isSomeValueShortened;
    }

    public override init(dataRow: IRowData, ownerGrid: IListDataGrid<CheckedDataRow>, controlElement: HTMLElement): void {
        if (this.checkAndLogDisposedUsage("init")) return;

        this._controlElement = controlElement;
        this._dataGrid = ownerGrid;
        this._dataCells = new Array<TableCell>();

        this.isDisabled = dataRow["disabled"] == true;

        this.dataKey = dataRow[this._dataGrid.dataSource.dataKey].valueOf();
        //this.DataKey = data
        //if (this.DataKey == undefined) throw Error("DataRow has invalid dataKey");

        const dataRowCaseInsensitive = ObjectUtils.getPropertiesCaseInsensitive<IAttrValue>(dataRow);
        for (let i: number = 0; i < this._dataGrid.columns.length; i++) {
            const column: Column = this._dataGrid.columns[i];
            // Kvuli LOVAttribute musi byt Object data jako pole, protoze obsahuje vic zobrazovatelnych atributu
            const data: Array<IAttrValue> = [];
            this._isSomeValueShortened = false;
            for (const d of column.attrNames) {
                const attrValue = dataRowCaseInsensitive.get(d);

                if (!this._isSomeValueShortened && attrValue instanceof IAttrStringValue && attrValue.shortened)
                    this._isSomeValueShortened = true;

                data.push(attrValue);
            }

            const cell: TableCell = new TableCell(this._dataGrid, this, i, column, data);
            if (!this.dataKey)
                this.dataKey = cell.dataSet[0].valueOf();

            this._dataCells.push(cell);
        }

        if (this.isDisabled)
            this.classList.add("isDisabled");

        //this.dataRow = row;
    }

    /**
     * Kliknuti na radek
     */
    protected async _clickEvt(_evt: MouseEvent): Promise<void> {
        if (this.checkAndLogDisposedUsage("_clickEvt")) return;

        if (!this._dataGrid.interactive)
            return;

        if (!(await this._checkIfCanSelectionChange()))
            return;

        this._dataGrid.focus();

        this.isSelected = !this.isSelected;

        this._dataGrid.dataRows.forEach(r => r.classList.remove("selectedRecord"));

        if (_evt.shiftKey) {
            let startIndex = 0;
            let endIndex = this._dataGrid.dataRows.indexOf(this);

            if (this._dataGrid.lastClickedRow != null) {
                startIndex = this._dataGrid.dataRows.indexOf(this._dataGrid.lastClickedRow);
            }

            if (startIndex > endIndex) {
                const tmp = startIndex;
                startIndex = endIndex;
                endIndex = tmp;
            }

            for (let i = startIndex; i <= endIndex; i++) {
                this._dataGrid.dataRows[i].isSelected = true;
                this._dispatchSelectionChangeEvent(this._dataGrid.dataRows[i].dataKey, { doubleClick: false, clickedRow: this._dataGrid.dataRows[i] });
            }

            this._dataGrid.lastClickedRow = this;
            this._dataGrid.lastClickedRow.classList.add("selectedRecord");

            return;
        }

        this._dataGrid.lastClickedRow = this;
        this._dataGrid.lastClickedRow.classList.add("selectedRecord");

        // pokud je vybrany jen jeden radek
        if (this._dataGrid.selectedRows.length == 1) {
            this._dispatchSelectionChangeEvent(this.dataKey, { doubleClick: false, clickedRow: this });
        } else { // neni vybrany zadny radek nebo oznaceno vice zaznamu
            this._dispatchSelectionChangeEvent("-1", { doubleClick: false, clickedRow: this });
        }
    }

    /**
     * Renders the control to the specified HTML writer.
     */
    public override render(): HTMLElement {
        if (this.checkAndLogDisposedUsage("render")) return undefined;

        this.innerHTML = "";

        this._createCells();

        return this as HTMLElement;
    }

    protected override _createFirstCell(): HTMLTableCellElement {
        if (this.checkAndLogDisposedUsage("_createFirstCell")) return undefined;

        const tableCell = super._createFirstCell();
        if (!this._dataGrid.renderControlElement) {
            tableCell.style.width = "0px";
            return tableCell;
        }

        const checkboxContainer = createElement({ type: "div", classes: "record-control-cell CheckBox" });

        this._checkboxElement = new Checkbox();
        this._checkboxElement.standardAction = true;
        this._checkboxElement.style.pointerEvents = "none";

        checkboxContainer.appendChild(this._checkboxElement as HTMLElement);

        //tableCell.insertBefore(checkboxContainer, tableCell.firstChild);

        // element s ovládacími prvky k datovému záznamu
        if (this._controlElement) {
            this._controlElement.insertBefore(checkboxContainer, this._controlElement.firstChild);
            tableCell.appendChild(this._controlElement);
        } else {
            tableCell.insertBefore(checkboxContainer, tableCell.firstChild);
        }

        return tableCell;
    }

    public override setItemType(): void {
        // Prázdná metoda
    }

    private _createCells(): void {
        if (this.checkAndLogDisposedUsage("_createCells")) return;

        const frozenRowHelper = createElement({ classes: "frozen-part-helper" });
        this._frozenPart = createElement({ classes: "frozen-part" });
        frozenRowHelper.appendChild(this._frozenPart);
        this.insertAdjacentElement("afterbegin", frozenRowHelper);

        this._frozenPart.appendChild(this._createFirstCell());

        for (const cell of this.dataCells) {
            if (cell.column.visible) {
                cell.toEdit(false);
                const newCell = cell.render();
                if (newCell) //sloupec mohl změnit viditelnost a render vrátil null.
                    this.appendChild(newCell);
            }
        }
    }

    //#endregion Methods

    //#region HTMLElement
    protected override _constructor(): void {
        super._constructor();

        this.classList.add("CheckedDataRow");

        this.addEventListener("click", (e: MouseEvent) => {
            this._clickEvt(e);
        });

        this.addEventListener("DoubleClick", () => {
            this._doubleClickEvt();
        });
    }

    //#endregion HTMLElement
} //end DataRow

registerCustomElement("tsc-CheckedDataRow", CheckedDataRow);
